import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { getUpload, getFeatures, getTileUrl, requestExport, getUploadExports, getExport } from '../../services/api'
import { Upload, Layer, GeoJSONCollection } from '../../types'
import LayerSwitcher from './LayerSwitcher'
import { ArrowLeft, Crosshair, Square, Pentagon, Download, X, FileImage, FileDigit, AlertCircle, Map as MapIcon } from 'lucide-react'

type DrawMode = 'none' | 'rectangle' | 'polygon';

export default function MapViewer() {
  const { uploadId } = useParams()
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<maplibregl.Map | null>(null)
  const [upload, setUpload] = useState<Upload | null>(null)
  const [layers, setLayers] = useState<Layer[]>([])
  const [activeLayer, setActiveLayer] = useState('orthophoto')
  const [features, setFeatures] = useState<GeoJSONCollection | null>(null)
  const [loading, setLoading] = useState(true)
  const [pixelInfo, setPixelInfo] = useState<{lat: number; lon: number} | null>(null)
  const [error, setError] = useState<string | null>(null)

  // ROI Drawing state
  const [drawMode, setDrawMode] = useState<DrawMode>('none')
  const [drawPoints, setDrawPoints] = useState<number[][]>([])

  const [showExportModal, setShowExportModal] = useState(false)
  const [exportFormat, setExportFormat] = useState('geotiff')
  const [exportLayer, setExportLayer] = useState('orthophoto')
  const [exportLoading, setExportLoading] = useState(false)
  const [exports, setExports] = useState<any[]>([])
  const [showExportsPanel, setShowExportsPanel] = useState(false)

    useEffect(() => {
    if (!uploadId) return
    console.log('MapViewer: loading upload', uploadId)
    Promise.all([
      getUpload(parseInt(uploadId)),
      getFeatures(parseInt(uploadId)),
      getUploadExports(parseInt(uploadId))
    ])
      .then(([uploadRes, featRes, exportRes]) => {
        console.log('MapViewer: upload loaded', uploadRes.data)
        setUpload(uploadRes.data.upload)
        setLayers(uploadRes.data.layers)
        setFeatures(featRes.data)
        setExports(exportRes.data.exports)
        if (uploadRes.data.layers.length > 0) {
          setExportLayer(uploadRes.data.layers[0].layer_name)
        }
      })
      .catch((err: any) => {
        console.error('MapViewer error:', err)
        const msg = err.response?.data?.error 
          || err.response?.data?.message 
          || err.message 
          || 'Failed to load upload data'
        setError(msg)
      })
      .finally(() => setLoading(false))
  }, [uploadId])

    // Initialize map
  useEffect(() => {
    if (!mapContainer.current || !upload || layers.length === 0) return

    const tileUrl = getTileUrl(upload.id, 'orthophoto')

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          satellite: {
            type: 'raster',
            tiles: ['https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}'],
            tileSize: 256,
            attribution: 'Google Satellite'
          },
          orthophoto: {
            type: 'raster',
            tiles: [tileUrl],
            tileSize: 256,
            attribution: 'DroneMap'
          },
          ...layers.filter(l => l.layer_name !== 'orthophoto').reduce((acc, l) => {
            acc[l.layer_name] = {
              type: 'raster',
              tiles: [getTileUrl(upload.id, l.layer_name)],
              tileSize: 256,
              attribution: l.layer_name.toUpperCase()
            }
            return acc
          }, {} as any),
          features: {
            type: 'geojson',
            data: features || { type: 'FeatureCollection', features: [] }
          },
          draw: {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] }
          }
        },

        layers: [
          {
            id: 'satellite',
            source: 'satellite',
            type: 'raster',
            paint: { 'raster-opacity': 1 }
          },
          {
            id: 'orthophoto-layer',
            source: 'orthophoto',
            type: 'raster',
            paint: { 'raster-opacity': 1 }
          },
          ...layers.filter(l => l.layer_name !== 'orthophoto').map(l => ({
            id: `${l.layer_name}-layer`,
            source: l.layer_name,
            type: 'raster' as const,
            paint: { 'raster-opacity': 0 },
            layout: { visibility: 'none' as const }
          })),
          {
            id: 'features-fill',
            source: 'features',
            type: 'fill',
            paint: { 'fill-color': '#F7941D', 'fill-opacity': 0.1 }
          },
          {
            id: 'features-outline',
            source: 'features',
            type: 'line',
            paint: { 'line-color': '#F7941D', 'line-width': 2 }
          },
          {
            id: 'features-points',
            source: 'features',
            type: 'circle',
            filter: ['==', ['geometry-type'], 'Point'],
            paint: {
              'circle-radius': 6,
              'circle-color': '#F7941D',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#fff'
            }
          },
          {
            id: 'draw-fill',
            source: 'draw',
            type: 'fill',
            paint: { 'fill-color': '#F7941D', 'fill-opacity': 0.2 }
          },
          {
            id: 'draw-line',
            source: 'draw',
            type: 'line',
            paint: { 'line-color': '#F7941D', 'line-width': 2, 'line-dasharray': [2, 2] }
          },
          {
            id: 'draw-points',
            source: 'draw',
            type: 'circle',
            paint: {
              'circle-radius': 5,
              'circle-color': '#fff',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#F7941D'
            }
          }
        ]



      },
      center: [0, 0],
      zoom: 2,
      maxZoom: 22,
      preserveDrawingBuffer: true
    })

    map.current.addControl(new maplibregl.NavigationControl(), 'top-right')
    map.current.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left')
    map.current.addControl(new maplibregl.FullscreenControl(), 'top-right')

    // Center on orthophoto WGS84 bounds when loaded
    map.current.on('load', () => {
      const meta = upload?.metadata;
      if (meta?.wgs84_bounds) {
        const b = meta.wgs84_bounds;
        const bounds: [[number, number], [number, number]] = [
          [b.west, b.south],
          [b.east, b.north]
        ];
        console.log('Fitting to WGS84 bounds:', bounds);
        map.current?.fitBounds(bounds, { padding: 50, maxZoom: 20, duration: 1000 });
      } else if (features && features.features.length > 0) {
        // Fallback: fit to features
        const coords: number[][] = []
        features.features.forEach(f => {
          const geom = f.geometry
          if (!geom) return
          if (geom.type === 'Point') {
            coords.push(geom.coordinates)
          } else if (geom.type === 'Polygon') {
            geom.coordinates[0].forEach((c: number[]) => coords.push(c))
          } else if (geom.type === 'LineString') {
            geom.coordinates.forEach((c: number[]) => coords.push(c))
          }
        })
        if (coords.length > 0) {
          const lons = coords.map(c => c[0])
          const lats = coords.map(c => c[1])
          const bounds: [[number, number], [number, number]] = [
            [Math.min(...lons), Math.min(...lats)],
            [Math.max(...lons), Math.max(...lats)]
          ]
          map.current?.fitBounds(bounds, { padding: 50, maxZoom: 18, duration: 1000 })
        }
      }
      
      map.current?.on('error', (e) => {
        console.error('MapLibre error:', e)
      })
    })

    // Click handler
    map.current.on('click', (e) => {
      const { lng, lat } = e.lngLat
      setPixelInfo({ lat, lon: lng })
      
      const pointFeatures = map.current?.queryRenderedFeatures(e.point, {
        layers: ['features-fill', 'features-points']
      })
      if (pointFeatures && pointFeatures.length > 0) {
        const feature = pointFeatures[0]
        new maplibregl.Popup()
          .setLngLat([lng, lat])
          .setHTML(`<div class="p-2"><h4 class="font-semibold text-navy-800">${feature.properties?.name || 'Feature'}</h4><p class="text-sm text-gray-500">${feature.properties?.feature_type || ''}</p></div>`)
          .addTo(map.current!)
      }
    })

    return () => {
      map.current?.remove()
    }
  }, [upload, layers, features])

  // Layer switching
  useEffect(() => {
    if (!map.current) return
    layers.forEach(l => {
      const layerId = `${l.layer_name}-layer`
      if (map.current?.getLayer(layerId)) {
        map.current.setLayoutProperty(layerId, 'visibility', l.layer_name === activeLayer ? 'visible' : 'none')
        map.current.setPaintProperty(layerId, 'raster-opacity', l.layer_name === activeLayer ? 1 : 0)
      }
    })
  }, [activeLayer, layers])

  // Drawing mode handlers
  useEffect(() => {
    if (!map.current) return

    const handleClick = (e: maplibregl.MapMouseEvent) => {
      if (drawMode === 'none') return

      const coords: [number, number] = [e.lngLat.lng, e.lngLat.lat]

      if (drawMode === 'rectangle') {
        if (drawPoints.length === 0) {
          setDrawPoints([coords])
        } else if (drawPoints.length === 1) {
          const p1 = drawPoints[0]
          const p2 = coords
          const rect = [
            [p1[0], p1[1]],
            [p2[0], p1[1]],
            [p2[0], p2[1]],
            [p1[0], p2[1]],
            [p1[0], p1[1]]
          ]
          updateDrawSource({ type: 'Polygon', coordinates: [rect] })
          setDrawPoints([])
          setDrawMode('none')
          setShowExportModal(true)
        }
      } else if (drawMode === 'polygon') {
        const newPoints = [...drawPoints, coords]
        setDrawPoints(newPoints)

        if (newPoints.length >= 3) {
          const closed = [...newPoints, newPoints[0]]
          updateDrawSource({ type: 'Polygon', coordinates: [closed] })
        } else {
          updateDrawSource({ type: 'LineString', coordinates: newPoints })
        }
      }
    }

    const handleDblClick = () => {
      if (drawMode === 'polygon' && drawPoints.length >= 3) {
        setDrawMode('none')
        setShowExportModal(true)
      }
    }

    const handleContextMenu = (e: maplibregl.MapMouseEvent) => {
      e.preventDefault()
      if (drawMode === 'polygon' && drawPoints.length >= 3) {
        setDrawMode('none')
        setShowExportModal(true)
      }
    }

    map.current.on('click', handleClick)
    map.current.on('dblclick', handleDblClick)
    map.current.on('contextmenu', handleContextMenu)

    // Cursor
    map.current.getCanvas().style.cursor = drawMode !== 'none' ? 'crosshair' : ''

    return () => {
      map.current?.off('click', handleClick)
      map.current?.off('dblclick', handleDblClick)
      map.current?.off('contextmenu', handleContextMenu)
    }
  }, [drawMode, drawPoints])

  const updateDrawSource = (geometry: any) => {
    const source = map.current?.getSource('draw') as maplibregl.GeoJSONSource
    if (source) {
      source.setData({
        type: 'FeatureCollection',
        features: geometry ? [{ type: 'Feature', properties: {}, geometry }] : []
      })
    }
  }

  const clearDraw = () => {
    setDrawPoints([])
    setDrawMode('none')
    updateDrawSource(null)
  }

  const getDrawGeoJSON = (): any => {
    const source = map.current?.getSource('draw') as maplibregl.GeoJSONSource
    if (!source) return null
    // We need to get the current data - since we track it in state for the rectangle,
    // and for polygon we have drawPoints
    if (drawPoints.length >= 3) {
      const closed = [...drawPoints, drawPoints[0]]
      return { type: 'Polygon', coordinates: [closed] }
    }
    return null
  }

  const handleExport = async () => {
    const geojson = getDrawGeoJSON()
    if (!geojson || !upload) return

    setExportLoading(true)
    try {
      const res = await requestExport({
        upload_id: upload.id,
        roi_geojson: geojson,
        layer_name: exportLayer,
        file_format: exportFormat,
      })

      setShowExportModal(false)
      clearDraw()

      // Poll for completion
      const exportId = res.data.id
      const poll = setInterval(async () => {
        try {
          const statusRes = await getExport(exportId)
          if (statusRes.data.status === 'completed') {
            clearInterval(poll)
            // Trigger download
            window.location.href = `/api/exports/${exportId}`
            // Refresh exports list
            const exportsRes = await getUploadExports(upload.id)
            setExports(exportsRes.data.exports)
          } else if (statusRes.data.status === 'failed') {
            clearInterval(poll)
            alert('Export failed: ' + statusRes.data.error_message)
          }
        } catch (e) {
          clearInterval(poll)
        }
      }, 3000)

      // Also refresh exports list immediately
      const exportsRes = await getUploadExports(upload.id)
      setExports(exportsRes.data.exports)

    } catch (err: any) {
      alert(err.response?.data?.error || 'Export request failed')
    } finally {
      setExportLoading(false)
    }
  }

    if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-gray-400">
        <AlertCircle size={48} className="text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Map</h2>
        <p className="text-sm text-gray-500 max-w-md text-center">{error}</p>
        <button 
          onClick={() => window.history.back()} 
          className="mt-4 btn-primary"
        >
          Go Back
        </button>
      </div>
    )
  }

  if (!upload) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-gray-400">
        <MapIcon size={48} className="mb-4" />
        <h2 className="text-xl font-semibold text-navy-800 mb-2">Upload Not Found</h2>
        <p className="text-sm text-gray-500">Upload ID: {uploadId}</p>
        <button 
          onClick={() => window.history.back()} 
          className="mt-4 btn-primary"
        >
          Go Back
        </button>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col -m-4 lg:-m-8">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={() => window.history.back()} className="p-2 hover:bg-gray-50 rounded-lg text-navy-600">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="font-semibold text-navy-800">{upload.original_filename || `Upload #${upload.id}`}</h2>
            <p className="text-xs text-gray-500">{layers.length} layers • {features?.features.length || 0} features</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Drawing tools */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => { clearDraw(); setDrawMode('rectangle'); }}
              className={`p-2 rounded-md transition-colors ${drawMode === 'rectangle' ? 'bg-orange-500 text-white' : 'text-navy-600 hover:bg-gray-200'}`}
              title="Draw Rectangle"
            >
              <Square size={16} />
            </button>
            <button
              onClick={() => { clearDraw(); setDrawMode('polygon'); }}
              className={`p-2 rounded-md transition-colors ${drawMode === 'polygon' ? 'bg-orange-500 text-white' : 'text-navy-600 hover:bg-gray-200'}`}
              title="Draw Polygon (click points, right-click to finish)"
            >
              <Pentagon size={16} />
            </button>
            <button
              onClick={clearDraw}
              className="p-2 rounded-md text-navy-600 hover:bg-gray-200 transition-colors"
              title="Clear"
            >
              <X size={16} />
            </button>
          </div>

          <LayerSwitcher layers={layers} activeLayer={activeLayer} onChange={setActiveLayer} />

          <button
            onClick={() => setShowExportsPanel(!showExportsPanel)}
            className="p-2 text-navy-600 hover:bg-gray-50 rounded-lg relative"
            title="Exports"
          >
            <Download size={18} />
            {exports.length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-[10px] rounded-full flex items-center justify-center">
                {exports.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Drawing mode indicator */}
      {drawMode !== 'none' && (
        <div className="bg-orange-50 border-b border-orange-100 px-4 py-2 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 text-sm text-orange-700">
            <Crosshair size={14} />
            <span className="font-medium">
              {drawMode === 'rectangle' ? 'Click two opposite corners to define rectangle' : 'Click points to define polygon, right-click to finish'}
            </span>
          </div>
          <button onClick={clearDraw} className="text-xs text-orange-600 hover:text-orange-700 font-medium">
            Cancel
          </button>
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 relative flex">
        {/* Map */}
        <div ref={mapContainer} className="flex-1" />

        {/* Exports panel */}
        {showExportsPanel && (
          <div className="w-80 bg-white border-l border-gray-100 flex flex-col shadow-lg z-20">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-navy-800">Exports</h3>
              <p className="text-xs text-gray-500 mt-1">ROI downloads for this upload</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {exports.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No exports yet. Draw a shape on the map to export.</p>
              ) : (
                exports.map(exp => (
                  <div key={exp.id} className="p-3 rounded-lg border border-gray-100 hover:border-orange-200 transition-colors">
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                        exp.status === 'completed' ? 'bg-green-50 text-green-600' :
                        exp.status === 'processing' ? 'bg-blue-50 text-blue-600' :
                        exp.status === 'failed' ? 'bg-red-50 text-red-600' :
                        'bg-yellow-50 text-yellow-600'
                      }`}>
                        {exp.status}
                      </span>
                      <span className="text-xs text-gray-400 uppercase">{exp.file_format}</span>
                    </div>
                    <p className="text-sm text-navy-700 mt-2">{exp.export_type}</p>
                    <p className="text-xs text-gray-400">{new Date(exp.created_at).toLocaleString()}</p>
                    {exp.status === 'completed' && (
                      <a
                        href={`/api/exports/${exp.id}`}
                        className="mt-2 inline-flex items-center text-xs text-orange-500 hover:text-orange-600 font-medium"
                      >
                        <Download size={12} className="mr-1" />
                        Download
                      </a>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Pixel info */}
        {pixelInfo && (
          <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-3 z-10 border border-gray-100">
            <div className="flex items-center gap-2 text-sm text-navy-700">
              <Crosshair size={14} className="text-orange-500" />
              <span>Lat: {pixelInfo.lat.toFixed(6)}</span>
              <span className="text-gray-300">|</span>
              <span>Lon: {pixelInfo.lon.toFixed(6)}</span>
            </div>
          </div>
        )}

        {/* Legend */}
        {activeLayer !== 'orthophoto' && (
          <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-4 z-10 border border-gray-100">
            <h4 className="text-sm font-semibold text-navy-800 mb-2 uppercase">{activeLayer}</h4>
            <div className="w-48 h-3 rounded-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500"></div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>-1.0</span><span>0.0</span><span>1.0</span>
            </div>
          </div>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-navy-800 mb-1">Export ROI</h3>
            <p className="text-sm text-gray-500 mb-4">Download the selected area as a georeferenced image.</p>

            <div className="space-y-4">
              <div>
                <label className="label">Source Layer</label>
                <select
                  value={exportLayer}
                  onChange={(e) => setExportLayer(e.target.value)}
                  className="input"
                >
                  {layers.filter(l => !l.layer_name.endsWith('_raw')).map(l => (
                    <option key={l.layer_name} value={l.layer_name}>
                      {l.layer_name.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">File Format</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'geotiff', label: 'GeoTIFF', icon: <FileDigit size={16} /> },
                    { value: 'jpeg', label: 'JPEG', icon: <FileImage size={16} /> },
                    { value: 'png', label: 'PNG', icon: <FileImage size={16} /> },
                  ].map(fmt => (
                    <button
                      key={fmt.value}
                      onClick={() => setExportFormat(fmt.value)}
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${
                        exportFormat === fmt.value
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-gray-100 hover:border-gray-200 text-navy-600'
                      }`}
                    >
                      {fmt.icon}
                      <span className="text-xs font-medium">{fmt.label}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  {exportFormat === 'geotiff' ? 'Full georeferenced raster for GIS use.' : 'Compressed image for reports and sharing.'}
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-6">
              <button
                onClick={() => { setShowExportModal(false); clearDraw(); }}
                className="btn-outline flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={exportLoading}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {exportLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Queuing...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Download size={16} />
                    Export & Download
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
